import React, { Component } from "react";
import { connect } from "react-redux";
import { Button, Form, Alert } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChartArea, 
  faSearch, 
  faChevronLeft, 
  faChevronRight,
  faSync,
  faArrowUp,
  faArrowDown,
  faChartLine
} from "@fortawesome/free-solid-svg-icons";
import axios from "axios";
import {
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ComposedChart,
  ResponsiveContainer,
  Line,
} from "recharts";
import dayjs from "dayjs";
import { fetchTrades } from "../../services/index";

class MtmReport extends Component {
  constructor(props) {
    super(props);
    this.state = {
      API_URL: process.env.REACT_APP_API_ENDPOINT
        ? process.env.REACT_APP_API_ENDPOINT.replace(/\/+$/, "") + "/"
        : "",
      autoRefreshEnabled: true,
      valueInPer: false,
      trades: [],
      report: [],
      chartData: [],
      chartDataP: [],
      search: "NIFTY 50",
      tradeDate: new Date().toISOString().slice(0, 10),
      currentPage: 1,
      tradesPerPage: 150,
      liveProfit: 0,
      liveProfitP: 0,
      maxProfit: 0,
      maxLoss: 0,
      maxProfitP: 0,
      maxLossP: 0,
      symbols: [
        { value: "NIFTY 50", display: "NIFTY" },
        { value: "NIFTY BANK", display: "BANK NIFTY" },
        { value: "NIFTY FIN SERVICE", display: "FINNIFTY" },
        { value: "SENSEX", display: "SENSEX" },
        { value: "BANKEX", display: "BANKEX" },
        { value: "NIFTY MID SELECT", display: "MIDCPNIFTY" },
      ],
      trade_types: [
        { value: "live", display: "Live" },
        { value: "paper", display: "Paper" },
      ],
      trade_type: "paper",
      algoNames: [
        { value: "CHEETAH_IOS", display: "Direction" },
        { value: "NON_DIRECTION", display: "Non Direction" },
        { value: "NON_DIRECTION_BTST", display: "NON DIRECTION BTST" },
        { value: "DIRECTION_BTST", display: "DIRECTION BTST" },
        { value: "DIRECTION_POSITIONAL", display: "DIRECTION POSITIONAL" },
        { value: "NON_DIRECTION_POSITIONAL", display: "NON DIRECTION POSITIONAL" },
      ],
      algoName: "CHEETAH_IOS",
      error: null,
      isLoading: false,
      isDataRefreshing: false,
    };
  }

  componentDidMount() {
    // Set default symbol based on current date
    const defaultSymbol = this.getDefaultSymbolForDate(this.state.tradeDate);
    this.setState({ search: defaultSymbol }, () => {
      this.findReport();
      if (this.state.autoRefreshEnabled) {
        this.startAutoRefresh();
      }
    });
  }

  componentWillUnmount() {
    this.stopAutoRefresh();
  }

  startAutoRefresh = () => {
    this.findReportInterval = setInterval(this.findReport, 15 * 1000);
  };

  stopAutoRefresh = () => {
    clearInterval(this.findReportInterval);
  };

  formatValue = (value) => {
    const absValue = Math.abs(value);
    if (absValue >= 10000000) {
      return `${(value / 10000000).toFixed(2)} Cr`;
    } else if (absValue >= 100000) {
      return `${(value / 100000).toFixed(2)} L`;
    } else if (absValue >= 1000) {
      return `${(value / 1000).toFixed(2)}k`;
    }
    return value.toFixed(2);
  };

  findReport = () => {
    const { API_URL, search, tradeDate, trade_type, valueInPer, algoName } = this.state;
    
    if (!this.state.report.value) {
      this.setState({ isLoading: true, error: null });
    } else {
      this.setState({ isDataRefreshing: true, error: null });
    }

    axios
      .get(
        `${API_URL}api/reportByIdV2?indexName=${search}&date=${tradeDate}&tradeType=${trade_type}&valueInPer=${valueInPer}&algoName=${algoName}`
      )
      .then((response) => {
        if (response.data && response.data.value && response.data.value.mtm_data) {
          const reportData = response.data;
          const mtmData = reportData.value.mtm_data;

          const values = Object.values(mtmData).map(value => parseFloat(value));
          const maxProfit = Math.max(...values);
          const maxLoss = Math.min(...values);
          const constvaluesP = Object.values(reportData.value.mtm_data_p).map(value => parseFloat(value));
          const maxProfitP = Math.max(...constvaluesP);
          const maxLossP = Math.min(...constvaluesP);
          
          const chartData = this.formatChartData(reportData.value.mtm_data);
          const chartDataP = this.formatChartData(reportData.value.mtm_data_p);
          
          this.setState({
            report: reportData,
            chartData,
            chartDataP,
            currentPage: 1,
            maxProfit: valueInPer ? maxProfit * 100 : maxProfit,
            maxLoss: valueInPer ? maxLoss * 100 : maxLoss,
            maxProfitP: maxProfitP,
            liveProfit: values.at(-1),
            liveProfitP: constvaluesP.at(-1),
            maxLossP: maxLossP,
            isLoading: false,
            isDataRefreshing: false,
          });
        } else {
          throw new Error("Invalid data format received from server");
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
        this.setState({
          error: "Failed to fetch data. Please try again later.",
          isLoading: false,
          isDataRefreshing: false,
        });
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("jwtToken");
          this.props.history.push("/");
        }
      });
  };

  searchChange = (event) => {
    this.setState({
      [event.target.name]: event.target.value,
    });
  };

  handleValueInPerChange = (event) => {
    this.setState({ valueInPer: event.target.checked }, this.findReport);
  };

  handleAutoRefreshChange = (event) => {
    const isChecked = event.target.checked;
    this.setState({ autoRefreshEnabled: isChecked });
    if (isChecked) {
      this.startAutoRefresh();
    } else {
      this.stopAutoRefresh();
    }
  };

  // Updated symbol selection logic based on SENSEX expiry schedule
  getDefaultSymbolForDate = (date) => {
    const selectedDate = dayjs(date);
    // Before June 2023: SENSEX data not available, always use NIFTY
    if (selectedDate.isBefore(dayjs("2023-06-01"))) {
      return "NIFTY 50";
    }
    const dayOfWeek = selectedDate.day(); // 0=Sunday, 1=Monday, 2=Tuesday, ..., 5=Friday
    const cutoffDate = dayjs("2025-09-01"); // September 2025
    
    // SENSEX expiry logic
    if (selectedDate.isBefore(cutoffDate)) {
      // Before Sept 2025: SENSEX expiry on Tuesday
      if (dayOfWeek === 2) {
        return "SENSEX"; // Tuesday = SENSEX expiry
      }
    } else {
      // After Aug 2025: SENSEX expiry on Friday
      if (dayOfWeek === 5) {
        return "SENSEX"; // Friday = SENSEX expiry
      }
    }
    
    // Default to NIFTY for all other days
    return "NIFTY 50";
  };

  handlePrevDate = () => {
    const prevDate = dayjs(this.state.tradeDate).subtract(1, "day").format("YYYY-MM-DD");
    const defaultSymbol = this.getDefaultSymbolForDate(prevDate);
    this.setState({ tradeDate: prevDate, search: defaultSymbol }, () => {
      this.findReport();
    });
  };

  handleNextDate = () => {
    const nextDate = dayjs(this.state.tradeDate).add(1, "day").format("YYYY-MM-DD");
    const defaultSymbol = this.getDefaultSymbolForDate(nextDate);
    this.setState({ tradeDate: nextDate, search: defaultSymbol }, () => {
      this.findReport();
    });
  };

  handleSevenPrevDate = () => {
    const prevDate = dayjs(this.state.tradeDate).subtract(7, "day").format("YYYY-MM-DD");
    const defaultSymbol = this.getDefaultSymbolForDate(prevDate);
    this.setState({ tradeDate: prevDate, search: defaultSymbol }, () => {
      this.findReport();
    });
  };

  handleSevenNextDate = () => {
    const nextDate = dayjs(this.state.tradeDate).add(7, "day").format("YYYY-MM-DD");
    const defaultSymbol = this.getDefaultSymbolForDate(nextDate);
    this.setState({ tradeDate: nextDate, search: defaultSymbol }, () => {
      this.findReport();
    });
  };

  formatChartData = (data) => {
    const { valueInPer } = this.state;
    return Object.entries(data).map(([time, value]) => {
      const currentValue = valueInPer ? parseFloat(value) * 100 : parseFloat(value);
      return {
        timestamp: time,
        value: currentValue,
        positiveValue: currentValue >= 0 ? currentValue : 0,
        negativeValue: currentValue < 0 ? currentValue : 0
      };
    });
  };

  // Custom tooltip for charts
  CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const value = payload.find(p => p.dataKey === 'value')?.value || 0;
      const isProfit = value >= 0;
      return (
        <div className="mtm-tooltip">
          <p className="tooltip-time">{label}</p>
          <p className={`tooltip-value ${isProfit ? 'profit' : 'loss'}`}>
            {this.state.valueInPer ? `${this.formatValue(value)}%` : `₹${this.formatValue(value)}`}
          </p>
        </div>
      );
    }
    return null;
  };

  render() {
    const { 
      search, 
      tradeDate, 
      trade_type, 
      autoRefreshEnabled, 
      error, 
      isLoading,
      isDataRefreshing,
      chartData,
      chartDataP,
      liveProfit,
      liveProfitP,
      maxProfit,
      maxLoss,
      maxProfitP,
      maxLossP
    } = this.state;

    const currentSymbol = this.state.symbols.find(s => s.value === search)?.display || search;

    return (
      <div className="mtm-container">
        {/* Header Section */}
        <div className="mtm-header">
          <div className="mtm-title">
            <FontAwesomeIcon icon={faChartArea} className="title-icon" />
            <h1>MTM Report</h1>
            <span className="symbol-badge">{currentSymbol}</span>
          </div>

          {/* Stats Cards */}
          <div className="stats-row">
            <div className={`stat-card ${liveProfit >= 0 ? 'profit' : 'loss'}`}>
              <div className="stat-icon">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Current MTM</span>
                <span className="stat-value">
                  ₹{this.formatValue(liveProfit || 0)}
                  <span className="stat-percent">({(liveProfitP || 0).toFixed(2)}%)</span>
                </span>
              </div>
            </div>

            <div className="stat-card profit">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faArrowUp} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Max Profit</span>
                <span className="stat-value">
                  ₹{this.formatValue(maxProfit || 0)}
                  <span className="stat-percent">({(maxProfitP || 0).toFixed(2)}%)</span>
                </span>
              </div>
            </div>

            <div className="stat-card loss">
              <div className="stat-icon">
                <FontAwesomeIcon icon={faArrowDown} />
              </div>
              <div className="stat-content">
                <span className="stat-label">Max Loss</span>
                <span className="stat-value">
                  ₹{this.formatValue(maxLoss || 0)}
                  <span className="stat-percent">({(maxLossP || 0).toFixed(2)}%)</span>
                </span>
              </div>
            </div>
          </div>

          {/* Controls Row */}
          <div className="controls-row">
            <div className="control-group">
              <label className="toggle-switch">
                <input 
                  type="checkbox" 
                  checked={autoRefreshEnabled} 
                  onChange={this.handleAutoRefreshChange} 
                />
                <span className="toggle-slider"></span>
                <span className="toggle-label">
                  <FontAwesomeIcon icon={faSync} className={autoRefreshEnabled ? 'spinning' : ''} />
                  Auto Refresh
                </span>
              </label>
            </div>

            <div className="control-group">
              <Form.Control 
                as="select" 
                name="trade_type" 
                value={trade_type} 
                onChange={this.searchChange}
                className="modern-select"
              >
                {this.state.trade_types.map((type) => (
                  <option key={type.value} value={type.value}>{type.display}</option>
                ))}
              </Form.Control>
            </div>

            <div className="control-group">
              <Form.Control 
                as="select" 
                name="search" 
                value={search} 
                onChange={this.searchChange}
                className="modern-select"
              >
                {this.state.symbols.map((symbol) => (
                  <option key={symbol.value} value={symbol.value}>{symbol.display}</option>
                ))}
              </Form.Control>
            </div>

            <div className="control-group">
              <Form.Control 
                type="date" 
                name="tradeDate" 
                value={tradeDate} 
                onChange={this.searchChange}
                className="modern-date"
              />
            </div>

            <div className="nav-buttons">
              <Button className="nav-btn" onClick={this.handleSevenPrevDate}>
                <FontAwesomeIcon icon={faChevronLeft} />
                <FontAwesomeIcon icon={faChevronLeft} />
                7
              </Button>
              <Button className="nav-btn" onClick={this.handlePrevDate}>
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>
              <Button className="nav-btn" onClick={this.handleNextDate}>
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
              <Button className="nav-btn" onClick={this.handleSevenNextDate}>
                7
                <FontAwesomeIcon icon={faChevronRight} />
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
              <Button className="search-btn" onClick={this.findReport}>
                <FontAwesomeIcon icon={faSearch} />
              </Button>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="mtm-content">
          {error && (
            <Alert variant="danger" dismissible onClose={() => this.setState({ error: null })} className="mtm-alert">
              {error}
            </Alert>
          )}

          {isLoading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading chart data...</p>
            </div>
          ) : (
            <div className="charts-wrapper">
              {isDataRefreshing && (
                <div className="refresh-badge">
                  <FontAwesomeIcon icon={faSync} spin /> Refreshing...
                </div>
              )}

              {/* Percentage Chart */}
              <div className="chart-card">
                <div className="chart-title">
                  <span className="chart-label">Percentage View</span>
                  <span className="chart-value">{(liveProfitP || 0).toFixed(2)}%</span>
                </div>
                <ResponsiveContainer width="100%" height={450}>
                  <ComposedChart data={chartDataP} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                      <linearGradient id="profitGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#10b981" stopOpacity={0.8}/>
                        <stop offset="100%" stopColor="#10b981" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="lossGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.1}/>
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0.8}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.5} />
                    <XAxis 
                      dataKey="timestamp" 
                      minTickGap={50} 
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      axisLine={{ stroke: '#334155' }}
                    />
                    <YAxis 
                      tickFormatter={(value) => `${value.toFixed(1)}%`} 
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      axisLine={{ stroke: '#334155' }}
                    />
                    <Tooltip content={<this.CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="positiveValue" 
                      fill="url(#profitGradient)" 
                      stroke="#10b981"
                      strokeWidth={0}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="negativeValue" 
                      fill="url(#lossGradient)" 
                      stroke="#ef4444"
                      strokeWidth={0}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#06b6d4" 
                      strokeWidth={2} 
                      dot={false}
                      filter="drop-shadow(0 0 4px rgba(6, 182, 212, 0.5))"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>

              {/* Absolute Value Chart */}
              <div className="chart-card">
                <div className="chart-title">
                  <span className="chart-label">Absolute Value</span>
                  <span className="chart-value">₹{this.formatValue(liveProfit || 0)}</span>
                </div>
                <ResponsiveContainer width="100%" height={450}>
                  <ComposedChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                    <defs>
                      <linearGradient id="profitGradient2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#22c55e" stopOpacity={0.8}/>
                        <stop offset="100%" stopColor="#22c55e" stopOpacity={0.1}/>
                      </linearGradient>
                      <linearGradient id="lossGradient2" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#f87171" stopOpacity={0.1}/>
                        <stop offset="100%" stopColor="#f87171" stopOpacity={0.8}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" opacity={0.5} />
                    <XAxis 
                      dataKey="timestamp" 
                      minTickGap={50} 
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      axisLine={{ stroke: '#334155' }}
                    />
                    <YAxis 
                      tickFormatter={(value) => this.formatValue(value)} 
                      tick={{ fill: '#94a3b8', fontSize: 11 }}
                      axisLine={{ stroke: '#334155' }}
                    />
                    <Tooltip content={<this.CustomTooltip />} />
                    <Area 
                      type="monotone" 
                      dataKey="positiveValue" 
                      fill="url(#profitGradient2)" 
                      stroke="#22c55e"
                      strokeWidth={0}
                    />
                    <Area 
                      type="monotone" 
                      dataKey="negativeValue" 
                      fill="url(#lossGradient2)" 
                      stroke="#f87171"
                      strokeWidth={0}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="value" 
                      stroke="#f59e0b" 
                      strokeWidth={2} 
                      dot={false}
                      filter="drop-shadow(0 0 4px rgba(245, 158, 11, 0.5))"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
        </div>

        <style>{`
          .mtm-container {
            padding: 24px;
            min-height: calc(100vh - 100px);
          }

          .mtm-header {
            background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
            border: 1px solid #334155;
            border-radius: 20px;
            padding: 28px;
            margin-bottom: 24px;
            box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
          }

          .mtm-title {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-bottom: 24px;
          }

          .mtm-title h1 {
            font-size: 28px;
            font-weight: 700;
            color: #f1f5f9;
            margin: 0;
          }

          .title-icon {
            font-size: 28px;
            color: #06b6d4;
          }

          .symbol-badge {
            background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
            color: white;
            padding: 6px 16px;
            border-radius: 20px;
            font-size: 14px;
            font-weight: 600;
          }

          /* Stats Row */
          .stats-row {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
            gap: 16px;
            margin-bottom: 24px;
          }

          .stat-card {
            background: rgba(15, 23, 42, 0.6);
            border: 1px solid #334155;
            border-radius: 16px;
            padding: 20px;
            display: flex;
            align-items: center;
            gap: 16px;
            transition: all 0.3s ease;
          }

          .stat-card:hover {
            transform: translateY(-2px);
            box-shadow: 0 10px 30px -10px rgba(0, 0, 0, 0.4);
          }

          .stat-card.profit {
            border-color: rgba(16, 185, 129, 0.3);
          }

          .stat-card.profit .stat-icon {
            background: rgba(16, 185, 129, 0.15);
            color: #10b981;
          }

          .stat-card.profit .stat-value {
            color: #10b981;
          }

          .stat-card.loss {
            border-color: rgba(239, 68, 68, 0.3);
          }

          .stat-card.loss .stat-icon {
            background: rgba(239, 68, 68, 0.15);
            color: #ef4444;
          }

          .stat-card.loss .stat-value {
            color: #ef4444;
          }

          .stat-icon {
            width: 52px;
            height: 52px;
            border-radius: 12px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 22px;
          }

          .stat-content {
            display: flex;
            flex-direction: column;
          }

          .stat-label {
            font-size: 13px;
            color: #64748b;
            margin-bottom: 4px;
          }

          .stat-value {
            font-size: 22px;
            font-weight: 700;
          }

          .stat-percent {
            font-size: 14px;
            font-weight: 500;
            margin-left: 8px;
            opacity: 0.8;
          }

          /* Controls Row */
          .controls-row {
            display: flex;
            align-items: center;
            gap: 16px;
            flex-wrap: wrap;
          }

          .control-group {
            display: flex;
            align-items: center;
          }

          .toggle-switch {
            display: flex;
            align-items: center;
            gap: 12px;
            cursor: pointer;
            user-select: none;
          }

          .toggle-switch input {
            display: none;
          }

          .toggle-slider {
            width: 44px;
            height: 24px;
            background: #334155;
            border-radius: 12px;
            position: relative;
            transition: all 0.3s ease;
          }

          .toggle-slider::before {
            content: '';
            position: absolute;
            width: 18px;
            height: 18px;
            background: #64748b;
            border-radius: 50%;
            top: 3px;
            left: 3px;
            transition: all 0.3s ease;
          }

          .toggle-switch input:checked + .toggle-slider {
            background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
          }

          .toggle-switch input:checked + .toggle-slider::before {
            transform: translateX(20px);
            background: white;
          }

          .toggle-label {
            color: #94a3b8;
            font-size: 14px;
            font-weight: 500;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .spinning {
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }

          .modern-select, .modern-date {
            background: #0f172a;
            border: 1px solid #334155;
            border-radius: 10px;
            color: #f1f5f9;
            padding: 10px 16px;
            font-size: 14px;
            min-width: 140px;
            cursor: pointer;
            transition: all 0.2s ease;
          }

          .modern-select:focus, .modern-date:focus {
            border-color: #06b6d4;
            box-shadow: 0 0 0 3px rgba(6, 182, 212, 0.15);
            outline: none;
          }

          .modern-select option {
            background: #1e293b;
            color: #f1f5f9;
          }

          .nav-buttons {
            display: flex;
            gap: 8px;
            margin-left: auto;
          }

          .nav-btn {
            background: rgba(15, 23, 42, 0.8);
            border: 1px solid #334155;
            color: #94a3b8;
            padding: 10px 16px;
            border-radius: 10px;
            font-size: 13px;
            display: flex;
            align-items: center;
            gap: 4px;
            transition: all 0.2s ease;
          }

          .nav-btn:hover {
            background: rgba(6, 182, 212, 0.1);
            border-color: #06b6d4;
            color: #06b6d4;
          }

          .search-btn {
            background: linear-gradient(135deg, #06b6d4 0%, #0891b2 100%);
            border: none;
            color: white;
            padding: 10px 20px;
            border-radius: 10px;
            font-size: 14px;
            transition: all 0.2s ease;
          }

          .search-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px -4px rgba(6, 182, 212, 0.5);
          }

          /* Content Section */
          .mtm-content {
            position: relative;
          }

          .mtm-alert {
            border-radius: 12px;
            margin-bottom: 24px;
          }

          .loading-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 80px;
            color: #64748b;
          }

          .loading-spinner {
            width: 48px;
            height: 48px;
            border: 3px solid #334155;
            border-top-color: #06b6d4;
            border-radius: 50%;
            animation: spin 1s linear infinite;
            margin-bottom: 16px;
          }

          .charts-wrapper {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(500px, 1fr));
            gap: 24px;
            position: relative;
          }

          .refresh-badge {
            position: absolute;
            top: 16px;
            right: 16px;
            background: rgba(6, 182, 212, 0.2);
            color: #06b6d4;
            padding: 8px 16px;
            border-radius: 20px;
            font-size: 13px;
            font-weight: 500;
            z-index: 10;
            display: flex;
            align-items: center;
            gap: 8px;
          }

          .chart-card {
            background: linear-gradient(145deg, #1e293b 0%, #0f172a 100%);
            border: 1px solid #334155;
            border-radius: 20px;
            padding: 24px;
            box-shadow: 0 10px 40px -10px rgba(0, 0, 0, 0.5);
          }

          .chart-title {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
            padding-bottom: 16px;
            border-bottom: 1px solid #334155;
          }

          .chart-label {
            font-size: 16px;
            font-weight: 600;
            color: #f1f5f9;
          }

          .chart-value {
            font-size: 20px;
            font-weight: 700;
            color: #06b6d4;
          }

          /* Custom Tooltip */
          .mtm-tooltip {
            background: rgba(15, 23, 42, 0.95);
            border: 1px solid #334155;
            border-radius: 12px;
            padding: 12px 16px;
            box-shadow: 0 10px 30px -5px rgba(0, 0, 0, 0.5);
          }

          .tooltip-time {
            font-size: 12px;
            color: #64748b;
            margin: 0 0 4px 0;
          }

          .tooltip-value {
            font-size: 18px;
            font-weight: 700;
            margin: 0;
          }

          .tooltip-value.profit {
            color: #10b981;
          }

          .tooltip-value.loss {
            color: #ef4444;
          }

          @media (max-width: 768px) {
            .mtm-container {
              padding: 16px;
            }

            .mtm-header {
              padding: 20px;
            }

            .mtm-title h1 {
              font-size: 22px;
            }

            .stats-row {
              grid-template-columns: 1fr;
            }

            .controls-row {
              flex-direction: column;
              align-items: stretch;
            }

            .nav-buttons {
              margin-left: 0;
              justify-content: center;
            }

            .charts-wrapper {
              grid-template-columns: 1fr;
            }
          }
        `}</style>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ tradeData: state.trade });

const mapDispatchToProps = (dispatch) => ({
  fetchTrades: () => dispatch(fetchTrades()),
});

export default connect(mapStateToProps, mapDispatchToProps)(MtmReport);
